import React from 'react'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'
import {StyleSheet, Text, View} from '@react-pdf/renderer'
import {IncomeDocumentTypes} from '../../../constants/Common'
import moment from 'moment'
import {find} from 'lodash'
import {numberFormat} from '../../../utils/Functions'

interface IPrintSignatureSection {
  document: IIncomeDocument
}

const PrintProductSection: React.FC<IPrintSignatureSection> = (props) => {
  const {document} = props
  const linkingDocument = document?.linkingDocument
  const isReceiptBilling =
    document.type === 'receipt' && document.linkingDocument?.billingDocuments?.length
  const isPR =
    document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER ||
    document.code?.startsWith('PR') ||
    document.code?.startsWith('ER')
  // ใบลด/เพิ่มหนี้ที่อ้าง AP/DP คัดลอกรายการผังบัญชีมาจากต้นทาง ต้องแสดงชื่อบัญชีเหมือน AP/DP —
  // หน้าบันทึก AP/DP ใส่ treatmentItemId: 1 เป็นค่าหลอก ถ้าแสดงแบบสินค้าจะขึ้น XRY001 "ถ่ายภาพรังสี…" (30 ก.ย. 2569)
  const isNoteOfApOrDp =
    [IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE, IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE].includes(
      document.type
    ) &&
    (['expense-expense-record', 'expense-deposit-payment'].includes(linkingDocument?.type) ||
      /^(AP|DP)/.test(linkingDocument?.code || ''))
  const isApOrDp = document.code?.startsWith('AP') || document.code?.startsWith('DP') || isNoteOfApOrDp
  // const isRR =
  //   document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD ||
  //   document.code?.startsWith('RR')

  const styles = StyleSheet.create({
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#E8EAFE',
      borderRadius: 4,
      paddingVertical: 6,
      paddingHorizontal: 10,
      fontWeight: 'bold',
    },
    headerCell: {
      flex: 1,
      textAlign: 'center',
      fontSize: 12,
    },
    row: {
      flexDirection: 'row',
      paddingVertical: 5,
      paddingHorizontal: 10,
      borderBottomWidth: 0.5,
      borderBottomColor: '#ccc',
    },
    leftDetail: {
      flex: 4,
      flexDirection: 'row',
      gap: 6,
    },
    image: {
      width: 40,
      height: 40,
      borderRadius: 20,
    },
    descriptionText: {
      fontSize: 12,
    },
    bold: {
      fontWeight: 'bold',
    },
    rightCell: {
      flex: 1,
      fontSize: 12,
      textAlign: 'center',
      alignSelf: 'center',
    },
  })

  const TableHeader = () => {
    if (document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER) {
      return (
        <View style={styles.tableHeader} fixed>
          {/*<Text style={[styles.headerCell, { flex: 4, textAlign: 'left' }]}>{'รายการ'}</Text>*/}
          <View style={{width: 30, textAlign: 'center'}}>
            <Text>{'No.'}</Text>
          </View>
          <View style={{width: 230, textAlign: 'center'}}>
            <Text>{'รายละเอียดสินค้า'}</Text>
          </View>
          <View style={{width: 70, textAlign: 'center'}}>
            <Text>{'คลัง'}</Text>
          </View>
          <View style={{width: 50, textAlign: 'right'}}>
            <Text>{'จำนวน'}</Text>
          </View>
          {!isPR && (
            <>
              <View style={{width: 80, textAlign: 'right'}}>
                <Text>{'หน่วยละ'}</Text>
              </View>
              <View style={{width: 70, textAlign: 'right'}}>
                <Text>{'ลด'}</Text>
              </View>
              <View style={{width: 70, textAlign: 'right'}}>
                <Text>{'จำนวนเงิน'}</Text>
              </View>
            </>
          )}
        </View>
      )
    }

    if (document.type === IncomeDocumentTypes.BILLING || isReceiptBilling) {
      return (
        <View style={styles.tableHeader} fixed>
          <Text style={[styles.headerCell, {textAlign: 'left'}]}>{'เลขที่เอกสาร'}</Text>
          <Text style={styles.headerCell}>{'วันที่เอกสาร'}</Text>
          <Text style={styles.headerCell}>{'จำนวนเงิน'}</Text>
        </View>
      )
    }

    return (
      <View style={styles.tableHeader} fixed>
        <Text style={[styles.headerCell, {flex: 4, textAlign: 'left'}]}>{'รายการ'}</Text>
        <Text style={styles.headerCell}>{'จำนวน'}</Text>
        {!isPR && (
          <>
            <Text style={styles.headerCell}>{'ราคาต่อหน่วย'}</Text>
            <Text style={styles.headerCell}>{'ส่วนลด'}</Text>
            <Text style={styles.headerCell}>{'จำนวนเงิน'}</Text>
          </>
        )}
      </View>
    )
  }

  const TableRow = ({product, index}: {product: any; index: number}) => {
    // REQ-002: รายการค่าธรรมเนียม/ปรับปรุง ไม่ผูกสินค้า จึงแสดงชื่อบัญชีแทนชื่อสินค้า
    const isAccountOnlyItem =
      !product.inventoryProductId && !product.treatmentItemId && !!product.accountCodeId
    const showAccountInfo = isApOrDp || isAccountOnlyItem
    const productUnit = showAccountInfo
      ? undefined
      : product.inventoryProduct?.unit || product.treatmentItem?.unit
    const productCode = showAccountInfo
      ? product.accountCodeItem?.code
      : product.inventoryProduct?.code ||
        product.treatmentItem?.barcode ||
        product.treatmentItem?.code
    const productName = showAccountInfo
      ? product.accountCodeItem?.name
      : product.inventoryProduct?.nameTh ||
        product.inventoryProduct?.nameEn ||
        product.treatmentItem?.nameTh ||
        product.treatmentItem?.nameEn
    const warehouseName = product.inventoryProduct?.warehouse?.name || '-'
    const originalItem = find(
      linkingDocument?.incomeDocumentItems,
      (i: any) =>
        (product.inventoryProductId && i.inventoryProductId === product.inventoryProductId) ||
        (product.treatmentItemId && i.treatmentItemId === product.treatmentItemId)
    )

    let pricePerUnit = product.pricePerUnit
    let discountPerUnit = product.discountPerUnit

    if (document.type === IncomeDocumentTypes.RECEIPT && originalItem) {
      pricePerUnit = originalItem.pricePerUnit
      discountPerUnit = originalItem.discountPerUnit
    }

    if (document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER) {
      return (
        <View wrap={false} style={styles.row}>
          <View style={{width: 30, textAlign: 'center'}}>
            <Text style={{fontWeight: 'bold'}}>{`${index + 1}.`}</Text>
          </View>
          <View style={{display: 'flex', flexDirection: 'column', width: 230}}>
            <View style={{display: 'flex', flexDirection: 'row', width: '100%', gap: '.5rem'}}>
              {/* รหัสเป็นบาร์โค้ด 13 หลักได้ — ห้ามหด ไม่งั้นทับชื่อสินค้า; ชื่อกินที่เหลือแล้วตัดบรรทัด ไม่ล้นไปช่องคลัง (29 ก.ย. 2569) */}
              <Text style={{minWidth: 50, flexShrink: 0, fontWeight: 'bold'}}>{`(${productCode})`}</Text>
              <Text style={{flex: 1}}>{productName}</Text>
            </View>
            {product?.note && (
              <Text style={styles.descriptionText}>{`หมายเหตุ : ${product?.note || '-'}`}</Text>
            )}
          </View>
          <View style={{width: 70, textAlign: 'right', marginRight: 10}}>
            <Text style={{fontSize: 10}}>{warehouseName}</Text>
          </View>
          <View style={{display: 'flex', flexDirection: 'row', width: 80, gap: '.5rem'}}>
            <Text style={{flex: 1, textAlign: 'right'}}>{numberFormat(product.quantity)}</Text>
            <Text style={{textAlign: 'right', width: 20}}>{productUnit || '-'}</Text>
          </View>
          {!isPR && (
            <>
              <View style={{width: 80, textAlign: 'right'}}>
                <Text>{numberFormat(pricePerUnit)}</Text>
              </View>
              <View style={{width: 70, textAlign: 'right'}}>
                <Text>{numberFormat(discountPerUnit)}</Text>
              </View>
              <View style={{width: 70, textAlign: 'right'}}>
                <Text>{numberFormat(product.quantity * product.pricePerUnit)}</Text>
              </View>
            </>
          )}
        </View>
      )
    }

    if (document.type === IncomeDocumentTypes.BILLING || isReceiptBilling) {
      const original = product.originalDocument

      return (
        <View style={styles.row}>
          <Text
            style={{
              ...styles.rightCell,
              fontWeight: 'bold',
              textAlign: 'left',
              paddingHorizontal: '8px',
            }}
          >{`${index + 1}. ${original.code}`}</Text>
          <Text style={styles.rightCell}>{moment(original.date).format('DD/MM/YYYY')}</Text>
          <Text style={styles.rightCell}>{numberFormat(product.billingAmount)}</Text>
        </View>
      )
    }

    return (
      <View wrap={false} style={styles.row}>
        <View style={styles.leftDetail}>
          <View style={{lineHeight: '13px'}}>
            <Text style={[styles.descriptionText, styles.bold]}>{`${
              index + 1
            }. ${productName}`}</Text>
            <Text style={styles.descriptionText}>
              {isAccountOnlyItem
                ? `รหัสบัญชี: ${productCode || '-'}`
                : `${isApOrDp ? 'รหัสบัญชี' : 'รหัสสินค้า'}: ${
                    productCode || '-'
                  }  |  คลัง: ${warehouseName}`}
            </Text>
            {product?.note && (
              <Text style={styles.descriptionText}>{`หมายเหตุ: ${product?.note || '-'}`}</Text>
            )}
          </View>
        </View>
        <Text style={styles.rightCell}>{numberFormat(product.quantity)}</Text>
        {!isPR && (
          <>
            <Text style={styles.rightCell}>{numberFormat(pricePerUnit)}</Text>
            <Text style={styles.rightCell}>{numberFormat(discountPerUnit)}</Text>
            <Text style={styles.rightCell}>
              {numberFormat(product.quantity * product.pricePerUnit)}
            </Text>
          </>
        )}
      </View>
    )
  }

  let products = document.incomeDocumentItems

  if (document.type === IncomeDocumentTypes.BILLING || isReceiptBilling) {
    products = document?.billingDocuments
    if (isReceiptBilling) {
      products = document.linkingDocument?.billingDocuments
    }
  }

  return (
    <View style={{marginTop: '10px'}}>
      <TableHeader />
      {products.map((product, index) => {
        return <TableRow key={`item-${index}`} product={product} index={index} />
      })}
    </View>
  )
}

export default PrintProductSection
